import { filter, sortBy, find, map, get } from 'lodash'
import { notify } from 'utils'
import moment from 'moment-timezone'
import { DATE_FORMAT, DATE_FORMAT_US } from '../../mixins'

export default {
  computed: {
    bookingHasTalentResponses () {
      return this.booking.talent_response_count &&
        this.booking.talent_response_count > 0
    },

    /**
     * Check if athlete leave time set for in person bookings
     *
     * @return string
     */
    bookingHasLeaveTime () {
      return this.booking.athlete_leave_time
    },

    /**
     * Class for if expenses covered.
     *
     * @return string
     */
    bookingExpensesCoveredClass () {
      return this.booking.expenses_covered
        ? 'yes'
        : 'no'
    },

    /**
     * Text for if expenses covered.
     *
     * @return string
     */
    bookingExpensesCovered () {
      return this.booking.expenses_covered
        ? 'Yes'
        : 'No'
    },

    /**
     * Class for if publicly promoted.
     *
     * @return string
     */
    bookingPromotedPubliclyClass () {
      return this.booking.promoted_publicly
        ? 'yes'
        : 'no'
    },

    /**
     * Text for if publicly promoted.
     *
     * @return string
     */
    bookingPromotedPublicly () {
      return this.booking.promoted_publicly
        ? 'Yes'
        : 'No'
    },

    /**
     * Class for if others can apply.
     *
     * @return string
     */
    bookingOthersCanApplyClass () {
      return this.booking.others_can_apply
        ? 'yes'
        : 'no'
    },

    /**
     * Text for if others can apply.
     *
     * @return string
     */
    bookingOthersCanApply () {
      return this.booking.others_can_apply
        ? 'Yes'
        : 'No'
    },

    /**
     * Booking display name.
     *
     * @return string
     */
    bookingName () {
      return this.booking.name
    },

    /**
     * Get the booking addtional location detail
     *
     * @return string
     */
    bookingAdditionalLocationDetail () {
      return this.booking.additional_location_detail
    },

    /**
     * Get the booking location latitude and longitude.
     *
     * @return null|array
     */
    bookingLatitudeAndLongitude () {
      if (this.booking.latitude && this.booking.longitude) {
        return {
          lat: this.booking.latitude,
          lng: this.booking.longitude
        }
      }

      return null
    },

    /**
     * Name of the bookings opportunity.
     *
     * If both in-person and online,
     * then opportunity_name is a concatenation
     * of both opportunity names.
     *
     * @return string
     */
    bookingOpportunityName () {
      return this.booking.opportunity_name
    },

    bookingOnlineOpportunityName () {
      return this.booking.online_opportunity
        ? this.booking.online_opportunity.name
        : null
    },

    bookingInPersonOpportunityName () {
      return this.booking.opportunity
        ? this.booking.opportunity.name
        : null
    },

    bookingCategoryName () {
      return this.booking.category
        ? this.booking.category.name
        : null
    },

    /**
     * Get the booking appearance type.
     *
     * @return string
     */
    bookingAppearanceType () {
      if (this.bookingAppearanceBoth) {
        return 'In Person and Online'
      } else if (this.bookingAppearanceInPerson) {
        return 'In Person'
      } else if (this.bookingAppearanceOnline) {
        return 'Online'
      }
    },

    /**
     * Determine if the booking is in person.
     *
     * This checks for a strict in person first and then checks if it's both as both includes in person.
     *
     * @return bool
     */
    bookingAppearanceInPerson () {
      return this.booking.appearance_type === 'in_person' ||
        this.bookingAppearanceBoth
    },

    /**
     * Determine if the booking is strictly online.
     *
     * @return bool
     */
    bookingAppearanceStrictlyOnline () {
      return this.booking.appearance_type === 'online'
    },

    /**
     * Determine if the booking is online.
     *
     * This checks for a strict online first and then checks if it's both as both includes online.
     *
     * @return bool
     */
    bookingAppearanceOnline () {
      return this.booking.appearance_type === 'online' ||
        this.bookingAppearanceBoth
    },

    /**
     * Determine if the booking appearance type is both.
     *
     * @return bool
     */
    bookingAppearanceBoth () {
      return this.booking.appearance_type === 'both'
    },

    /**
     * Determine if the booking appearance type is offer.
     *
     * @return bool
     */
    bookingAppearanceOffer () {
      return this.booking.appearance_type === 'offer'
    },

    /**
     * Determine if the booking is a booking type.
     *
     * @return bool
     */
    bookingTypeIsBooking () {
      return this.booking.type === 'booking'
    },

    /**
     * Determine if the booking is a booking request type.
     *
     * @return bool
     */
    bookingTypeIsBookingRequest () {
      return this.booking.type === 'request'
    },

    bookingTypeIsBookingRequestOrDraft () {
      return ['request', 'draft'].includes(this.booking.type)
    },

    /**
     * Determine if the booking belongs to the longed in user.
     *
     * @return bool
     */
    bookingIsMine () {
      return this.booking.client_id === this.$user.id
    },

    /**
     * Determine if the booking is vetting.
     *
     * @return bool
     */
    bookingIsVetting () {
      return this.booking.status === 'vetting'
    },

    /**
     * Determine if the booking is no longer vetting.
     *
     * @return bool
     */
    bookingIsNotVetting () {
      return this.booking.status !== 'vetting'
    },

    /**
     * Determine if the booking is a draft.
     *
     * @return bool
     */
    bookingIsDraft () {
      return this.booking.type === 'draft'
    },

    /**
     * Determine if the booking is an archived draft.
     *
     * @return bool
     */
    bookingIsArchiveDraft () {
      return (this.booking.type === 'draft' && this.booking.status === 'archived')
    },

    /**
     * Determine if the booking is not an archived draft.
     *
     * @return bool
     */
    bookingIsNotArchiveDraft () {
      return (this.booking.type === 'draft' && this.booking.status !== 'archived')
    },

    /**
     * Determine if the booking is pending.
     *
     * @return bool
     */
    bookingIsPending () {
      return this.booking.status === 'pending'
    },

    /**
     * Determine if the booking is pending.
     *
     * @return bool
     */
    bookingIsNotPending () {
      return !this.bookingIsPending
    },

    /**
     * Determine if the booking is withdrawn.
     *
     * @return bool
     */
    bookingIsWithdrawn () {
      if (this.booking.expires && (moment().isSameOrAfter(moment(this.booking.billboard_expiry_date, 'YYYY-MM-DD HH:mm:ss')))) {
        return false
      }
      return this.booking.status === 'withdrawn'
    },

    /**
     * Return the reason a booking was withdrawn
     *
     * @return string
     */
    bookingWithdrawReason () {
      let reasonsLookup = {
        budget: 'Budget Issues',
        applicants: 'No Suitable Applicants',
        talent_uninterested: 'Talent Not Interested',
        client_uninterested: 'Client Not Interested In Applied Talent',
        not_proceeding: 'Opportunity Not Proceeding',
        elsewhere: 'Talent Sourced Elsewhere',
        pickstar: this.clientName + ' Withdrew',
        call: 'Did Not Return Calls',
        info: 'Only Wanted Information',
        covid19: 'COVID-19',
        scheduling: 'Scheduling Issue',
        no_replacement: 'No Replacement Found',
        accidental_booking: 'Accidental Booking',
        talent_withdrawal: 'Talent Withdrawal',
        client_withdrawal: 'Client Withdrawal',
        expired: 'Expired',
        test: 'Admin Test',
        other: 'Other'
      }
      return reasonsLookup[this.booking.withdraw_reason] ? reasonsLookup[this.booking.withdraw_reason] : 'No Reason Given'
    },

    /**
     * Determine if the booking is not withdrawn.
     *
     * @return bool
     */
    bookingIsNotWithdrawn () {
      return !this.bookingIsWithdrawn
    },

    /**
     * Determine if the booking is declined.
     *
     * @return bool
     */
    bookingIsDeclined () {
      return this.booking.status === 'declined'
    },

    /**
     * Determine if the booking is not declined.
     *
     * @return bool
     */
    bookingIsNotDeclined () {
      return !this.bookingIsDeclined
    },

    /**
     * Determine if the booking is scheduled.
     *
     * @return bool
     */
    bookingIsScheduled () {
      return this.booking.status === 'scheduled'
    },

    /**
     * Determine if the booking is on hold.
     *
     * @return bool
     */
    bookingIsOnHold () {
      return this.booking.status === 'hold'
    },

    /**
     * Determine if the booking is not on hold.
     *
     * @return bool
     */
    bookingIsNotOnHold () {
      return !this.bookingIsOnHold
    },

    /**
     * Determine if the booking is booked.
     *
     * @return bool
     */
    bookingIsBooked () {
      return this.booking.status === 'booked'
    },

    /**
     * Determine if the booking is not booked.
     *
     * @return bool
     */
    bookingIsNotBooked () {
      return this.booking.status !== 'booked'
    },

    /**
     * Determine if the booking is completed.
     *
     * @return bool
     */
    bookingIsCompleted () {
      return this.booking.status === 'completed'
    },

    /**
     * Determine if the booking is booked with an unpaid deposit.
     *
     * @return bool
     */
    bookingBookedDepositUnpaid () {
      return (this.booking.status === 'booked' && this.$userIsAdmin && this.bookingPaymentDepositIsUnpaid)
    },

    /**
     * Determine if the booking is booked with a deposit sent.
     *
     * @return bool
     */
    bookingBookedDepositSent () {
      return (this.booking.status === 'booked' && this.$userIsAdmin && this.bookingPaymentDepositIsSent)
    },

    /**
     * Determine if the booking is not completed.
     *
     * @return bool
     */
    bookingIsNotCompleted () {
      return !this.bookingIsCompleted
    },

    /**
     * Determine if the booking is cancelled.
     *
     * @return bool
     */
    bookingIsCancelled () {
      return this.booking.status === 'cancelled'
    },

    /**
     * Determine if the booking is not cancelled.
     *
     * @return bool
     */
    bookingIsNotCancelled () {
      return !this.bookingIsCancelled
    },

    /**
     * Determine if bookings contextual status is cancelled.
     *
     * @return bool
     */
    bookingIsContextuallyCancelled () {
      return this.booking.contextual_status === 'cancelled'
    },

    /**
     * Determine if bookings contextual status is unsuccessful.
     *
     * @return bool
     */
    bookingIsContextuallyUnsuccessful () {
      return this.booking.contextual_status === 'unsuccessful'
    },

    /**
     * Determine if bookings contextual status is declined.
     *
     * @return bool
     */
    bookingIsContextuallyDeclined () {
      return this.booking.contextual_status === 'declined'
    },

    /**
     * Determine if bookings contextual status is applied.
     *
     * @return bool
     */
    bookingIsContextuallyApplied () {
      return this.booking.contextual_status === 'applied'
    },

    /**
     * Determine if the booking is public (on the billboard).
     *
     * @return bool
     */
    bookingIsPublic () {
      return this.booking.type === 'booking' || this.booking.others_can_apply
    },

    /**
     * Determine if the booking is not public (not on the billboard).
     *
     * @return bool
     */
    bookingIsNotPublic () {
      return !this.bookingIsPublic
    },

    /**
     * Show the state of the booking if it is an in person appearance (or both), otherwise show that
     * the booking is taking place "online".
     *
     * @return string
     */
    bookingStateOrOnline () {
      if (this.bookingAppearanceInPerson) {
        return this.booking.state
      }

      return 'Online'
    },

    /**
     * Determine if the booking terms and conditions have not been accepted.
     *
     * @return bool
     */
    bookingTermsAreNotAccepted () {
      return this.booking.client_accepted_terms === false
    },

    /**
     * Determine if the booking terms and conditions have been accepted.
     *
     * @return bool
     */
    bookingTermsAreAccepted () {
      return this.booking.client_accepted_terms === true
    },

    /**
     * Determine if talent can confirm their attendance based on the date of the event
     * and the current date and time.
     *
     * If no date OR the booking is strictly online only then always return true.
     *
     * @return boolean
     */
    bookingTalentAttendanceConfirmationIsOpen () {
      if (!this.booking.tz_date || this.bookingAppearanceOnline) {
        return true
      }

      return moment.tz(this.booking.tz_date, this.booking.timezone).isSameOrBefore(moment.tz(moment(), this.booking.timezone).add(24, 'hours'))
    },

    enabledPaymentMethodOptions () {
      let paymentMethods = [
        {id: 'bank_transfer', text: 'Bank Transfer'},
        {id: 'cheque', text: 'Cheque'},
        {id: 'existing_card', text: 'My Saved Cards'}
      ]

      if (this.booking.region.shortname === 'au') {
        paymentMethods = [...paymentMethods, {id: 'credit_card', text: 'Credit Card'}]
      }

      return paymentMethods
    },

    /**
     * Get the booking total budget.
     *
     * @return float
     */
    bookingGrandTotalBudget () {
      if (this.booking.budget_per_star === undefined) {
        return null
      }
      let amount = this.booking.budget_per_star
      if (this.booking.local_currency && this.booking.local_currency_budget_per_star) {
        amount = this.booking.local_currency_budget_per_star
      }
      if (this.bookingTypeIsBookingRequestOrDraft) {
        if (this.booking.required_stars) {
          return amount * this.booking.required_stars
        }
        return amount
      }

      return amount * this.bookingAttendingTalent.length
    },

    /**
     * Get the bookings budget.
     *
     * @return float
     */
    bookingBudget () {
      if (this.$userIsTalent || this.$userIsManager) {
        return this.booking.budget_per_star_as_user_rate
      }

      return this.booking.budget_per_star
    },

    /**
     * Determine if the booking date is the same date as today or after.
     *
     * If no date OR the booking is strictly online only then always return true.
     *
     * @return bool
     */
    bookingDateIsToday () {
      if (!this.booking.tz_date || this.bookingAppearanceStrictlyOnline) {
        return true
      }

      return moment.tz(moment(), this.booking.timezone).startOf('day').isSameOrAfter(moment.tz(this.booking.tz_date, this.booking.timezone).startOf('day'))
    },

    bookingInPersonDayBefore () {
      return moment.tz(this.booking.tz_date, this.booking.timezone).isSameOrBefore(moment.tz(moment(), this.booking.timezone).add(24, 'hours'))
    },

    /**
     * Determine if the booking date is the same date as today exact.
     *
     * If no date OR the booking is strictly online only then always return true.
     *
     * @return bool
     */
    bookingDateIsTodayExactly () {
      if (!this.booking.tz_date || this.bookingAppearanceStrictlyOnline) {
        return true
      }

      return moment.tz(moment(), this.booking.timezone).isBetween(
        moment.tz(this.booking.tz_date, this.booking.timezone).utc().startOf('day'),
        moment.tz(this.booking.tz_date, this.booking.timezone).utc().endOf('day')
      )
    },

    bookingDateHasPassed () {
      let bookingDate = this.booking.date
      if (this.$vlastMode && this.$allowBookingDateRange) {
        bookingDate = this.booking.mid_date_range ? this.booking.mid_date_range : this.booking.utc_date
      }
      return moment.utc(bookingDate) <= moment.utc() ||
        this.booking.appearance_type === 'online'
    },

    /**
     * Get booking talent that were shortlisted, sorted by priority in ascending order.
     *
     * @return array
     */
    bookingShortlistedTalent () {
      return sortBy(filter(this.booking.talent, talent => talent.response.shortlisted), 'response.priority')
    },

    /**
     * Get booking talent that are attending.
     *
     * @return array
     */
    bookingAttendingTalent () {
      return filter(this.booking.talent, (talent) => {
        return talent.response.client_accepted && (
          talent.response.status === 'applied' ||
          talent.response.status === 'cancelled'
        )
      })
    },

    /**
     * Get booking talent that are applied.
     *
     * @return array
     */
    bookingAppliedTalent () {
      return filter(this.booking.talent, (talent) => {
        return talent.response.status === 'applied'
      })
    },

    /**
     * Get booking talent that are attending that need to confirm attendance.
     *
     * @return array
     */
    bookingAttendingTalentNeedingConfirmation () {
      return filter(this.bookingAttendingTalent, talent => talent.response.attendance_confirmed === false)
    },

    /**
     * Determine if the booking has no shortlisted talent.
     *
     * @return bool
     */
    bookingHasNoShortlistedTalent () {
      return this.bookingShortlistedTalent.length === 0
    },

    /**
     * Get the current user from the booking talent if they exist.
     *
     * @return undefined|object
     */
    bookingCurrentTalent () {
      return find(this.booking.talent, talent => talent.id === this.$user.id)
    },

    /**
     * Show the bookings client company name and the clients name, or just the client name if no company.
     *
     * @returns string|null
     */
    bookingClientCompanyAndName () {
      if (!this.booking.client) {
        return null
      }
      if (this.$userIsTalent || this.$userIsManager) {
        return this.booking.client.company
      } else if (this.booking.client.company && this.booking.client.name) {
        return `${this.booking.client.company} / ${this.booking.client.name}`
      }

      return this.booking.client.company || this.booking.client.name
    },

    /**
     * Get the deposit payment from the booking.
     *
     * @return object
     */
    bookingPaymentDeposit () {
      return find(this.booking.payments, payment => payment.type === 'deposit')
    },

    /**
     * Get the final payment from the booking.
     *
     * @return object
     */
    bookingPaymentFinal () {
      return find(this.booking.payments, payment => payment.type === 'final')
    },

    /**
     * Get the final payment from the booking.
     *
     * @return object
     */
    bookingPaymentFull () {
      return find(this.booking.payments, payment => payment.type === 'full')
    },

    /**
     * Get the total payment amount for the deposit + final.
     *
     * @return float
     */
    bookingPaymentTotalAmount () {
      return this.bookingPaymentDeposit.total_inclusive + this.bookingPaymentFinal.total_inclusive
    },

    /**
     * Determine if deposit is unpaid.
     *
     * @return bool
     */
    bookingPaymentDepositIsUnpaid () {
      return get(this.bookingPaymentDeposit, 'status') === 'unpaid'
    },

    /**
     * Determine if deposit is unpaid.
     *
     * @return bool
     */
    bookingPaymentFullIsUnpaid () {
      return get(this.bookingPaymentFull, 'status') === 'unpaid'
    },

    /**
     * Determine if both payments are required.
     * They are both required if it is less than two weeks until the event.
     *
     * @return bool
     */
    bookingPaymentRequiresBoth () {
      let bookingDate = this.booking.date
      if (this.$vlastMode && this.$allowBookingDateRange) {
        bookingDate = this.booking.mid_date_range ? this.booking.mid_date_range : this.booking.utc_date
      }
      return (this.bookingPaymentDepositIsUnpaid || this.bookingPaymentDepositIsSent) && (this.bookingPaymentFinalIsUnpaid || this.bookingPaymentFinalIsSent) &&
        moment().startOf('day').isAfter(moment(bookingDate, 'YYYY-MM-DD HH:mm:ss').startOf('day').subtract(14, 'day'))
    },

    bookingInitialPaymentIsPaid () {
      return this.bookingPaymentDepositIsPaid || this.bookingPaymentFullIsPaid
    },

    /**
     * Determine if deposit is paid.
     *
     * @return bool
     */
    bookingPaymentDepositIsPaid () {
      return get(this.bookingPaymentDeposit, 'status') === 'paid'
    },

    /**
     * Determine if deposit is pending.
     *
     * @return bool
     */
    bookingPaymentDepositIsPending () {
      return get(this.bookingPaymentDeposit, 'status') === 'pending'
    },

    /**
     * Determine if final is unpaid.
     *
     * @return bool
     */
    bookingPaymentFinalIsUnpaid () {
      return get(this.bookingPaymentFinal, 'status') === 'unpaid'
    },

    /**
     * Determine if final is paid.
     *
     * @return bool
     */
    bookingPaymentFinalIsPaid () {
      return get(this.bookingPaymentFinal, 'status') === 'paid'
    },

    /**
     * Determine if final is pending.
     *
     * @return bool
     */
    bookingPaymentFinalIsPending () {
      return get(this.bookingPaymentFinal, 'status') === 'pending'
    },

    bookingIsFullyPaid () {
      return (this.bookingPaymentFinalIsPaid && this.bookingPaymentDepositIsPaid) || this.bookingPaymentFullIsPaid
    },

    bookingIsFullyPending () {
      return (this.bookingPaymentFinalIsPending && this.bookingPaymentDepositIsPending) || this.bookingPaymentFullIsPending
    },

    /**
     * Determine if final is paid.
     *
     * @return bool
     */
    bookingPaymentFullIsPaid () {
      return get(this.bookingPaymentFull, 'status') === 'paid'
    },

    /**
     * Determine if final is paid.
     *
     * @return bool
     */
    bookingPaymentFullIsPending () {
      return get(this.bookingPaymentFull, 'status') === 'pending'
    },

    /**
     * Determine if deposit is sent.
     *
     * @return bool
     */
    bookingPaymentDepositIsSent () {
      return get(this.bookingPaymentDeposit, 'status') === 'sent'
    },

    /**
     * Determine if final is sent.
     *
     * @return bool
     */
    bookingPaymentFinalIsSent () {
      return get(this.bookingPaymentFinal, 'status') === 'sent'
    },

    /**
     * Determine if final is sent.
     *
     * @return bool
     */
    bookingPaymentFullIsSent () {
      return get(this.bookingPaymentFull, 'status') === 'sent'
    },

    /**
     * Determine if the booking has expired off the billboard.
     *
     * @return bool
     */
    bookingHasExpired () {
      if (this.booking.expires) {
        return moment().isSameOrAfter(moment(this.booking.billboard_expiry_date, 'YYYY-MM-DD HH:mm:ss').day(14))
      }
      if (this.bookingAppearanceStrictlyOnline) {
        if (this.booking.date_flexible_or_unconfirmed) {
          return false
        } else {
          return this.booking.complete_by_date && moment.utc().isSameOrAfter(moment.utc(this.booking.complete_by_date, 'YYYY-MM-DD').day(14))
        }
      }
      return this.booking.utc_date && moment.utc().isSameOrAfter(moment.utc(this.booking.utc_date, 'YYYY-MM-DD HH:mm:ss').day(14))
    },

    /**
     * Maximum of 2 talent names separated by commas with plus X more.
     *
     * @return string
     */
    bookingTalentNames () {
      let names = map(this.booking.talent, 'name')

      if (names.length === 2) {
        return names.join(' and ')
      } else if (names.length > 2) {
        return `${names.slice(0, 2).join(', ')} (+${names.slice(2).length} more)`
      }

      return names.join(', ')
    },

    /**
     * Maximum of 2 talent names separated by commas with plus X more.
     * Finds only talent that are booked
     *
     * @return string
     */
    bookingBookedTalentNames () {
      let talent = filter(this.booking.talent, (talent) => {
        return talent.response.client_accepted && (
          talent.response.status === 'applied' ||
          talent.response.status === 'cancelled'
        )
      })
      let names = map(talent, 'name')

      if (names.length === 2) {
        return names.join(' and ')
      } else if (names.length > 2) {
        return `${names.slice(0, 2).join(', ')} (+${names.slice(2).length} more)`
      }

      return names.join(', ')
    },

    /**
     *  Checks if a booking has any selected talent
     *
     * @returns {boolean}
     */
    bookingHasSelectedTalent () {
      let talent = this.booking.talent
      let i
      for (i = 0; i < talent.length; i++) {
        if (talent[i].response.client_accepted) {
          return true
        }
      }
      return false
    },

    /**
     *  Checks if a booking has the amount of talent required, selected
     *
     * @returns {boolean}
     */

    bookingHasAllSelectedTalent () {
      let talent = this.booking.talent
      let i
      let required = this.booking.required_stars
      let selectedCount = 0
      for (i = 0; i < talent.length; i++) {
        if (talent[i].response.client_accepted) {
          selectedCount++
        }
        if (selectedCount === required) {
          return true
        }
      }
      return false
    },

    bookingInPersonCompleted () {
      return this.booking.talent[0] && this.booking.talent[0].response.in_person_completed_date !== null
    },

    bookingOnlineCompleted () {
      return this.booking.talent[0] && this.booking.talent[0].response.online_completed_date !== null
    },

    bookingOnlineCompletionDate () {
      let format = DATE_FORMAT
      if (this.$user.date_format_preference && this.$user.date_format_preference === 'month-first') {
        format = DATE_FORMAT_US
      }
      return this.bookingOnlineCompleted
        ? 'Completed: ' + moment.utc(this.booking.talent[0].response.online_completed_date).local().format(format)
        : 'Completed: ' + moment.utc(this.booking.completed_date).local().format(format)
    },

    /**
   *  Get assigned admin name
   *
   * @returns null/stringR
   */
    bookingAssignedAdmin () {
      return this.booking.admin ? this.booking.admin.name : 'Unassigned'
    },

    bookingIsStandardPackage () {
      return this.booking.package && this.booking.package.payment_type === 'standard'
    },

    userCanSeeBudget () {
      return this.$costReferences && (this.booking.budget_per_star_as_user_rate !== undefined || !(this.$userIsTalent || this.$userIsManager))
    }
  },

  methods: {
    /**
     * Handle HTTP exceptions when a booking cannot be found.
     *
     * This is used by many of the single components when initially loading a booking/request.
     *
     * Will re-throw the error if it was not a 404.
     *
     * @return void
     */
    handleCaughtHttpExceptionForBooking (error, notificationMessage = 'The booking could not be found.') {
      if (error.response && error.response.status === 404) {
        notify.error(notificationMessage)

        this.$router.push({name: 'dashboard', query: { src: 'mixin' }})

        return
      }

      // TODO: Handle this error a bit better.
      console.warn(error)
    },

    fieldSetSelected (fieldSet) {
      return this.booking.opportunities.findIndex(item => item.field_set === fieldSet) !== -1
    }
  }
}
